import { CommonModule, Location } from '@angular/common';
import { ChangeDetectorRef, Component, Input, NgModule, OnDestroy, OnInit } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import {
    IFindLeasingCalculator,
    IImage,
    IPremiumContentSelection,
    IProductPageResponse,
    IProductUspViewModel,
    IProductViewModel,
    ISeezFinanceCalculator,
    ISettingsResponse,
    ProductLeasingType,
} from '@ncg/data';
import { NgxJsonLdModule } from '@ngx-lite/json-ld';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { combineLatest, firstValueFrom, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators';
import { SwiperModule } from 'swiper/angular';
import { FavoritesPageLinkComponent } from '../../$lazy-content/components/favorites/favorites-page-link/favorites-page-link.component';
import { LazyContentModule } from '../../$lazy-content/lazy-content.module';
import { LazyContentAliases } from '../../$lazy-content/models/lazy-content.models';
import { FeatureDetectionService } from '../../core/feature-detection.service';
import { MetaService } from '../../core/meta.service';
import { SeezService } from '../../core/seez.service';
import { SettingsService } from '../../core/settings.service';
import { TrackingService } from '../../core/tracking.service';
import { DealerModule } from '../../dealer/dealer.module';
import { ExpansionPanelModule } from '../../expansion-panel/expansion-panel.module';
import { expansionAnimation } from '../../expansion-panel/expansion.animation';
import { FavoritesServiceModule } from '../../favorites-service/favorites-service.module';
import { ProductPageService } from '../../page/product-page/product-page.service';
import { RichTextModule } from '../../rich-text/rich-text.module';
import { SidePanelService } from '../../side-panel/side-panel.service';
import { SpotsUiModule } from '../../spots/spots-ui.module';
import { ImageUrl } from '../../utils/helpers/image-helper';
import { UtilsModule } from '../../utils/utils.module';
import { ProductItemTagsComponent } from '../product-item/product-item-tags.component';
import { GetPriceButtonComponent } from './product-buttons/get-price-button.component';
import { SeezBuyButtonComponent } from './product-buttons/seez-buy-button.component';
import { SeezFinanceCalculatorButtonComponent } from './product-buttons/seez-finance-calculator-button.component';
import { SendOfferButtonComponent } from './product-buttons/send-offer-button.component';
import { TestDriveButtonComponent } from './product-buttons/test-drive-button.component';
import { ProductGalleryCarouselComponent } from './product-gallery/product-gallery-carousel.component';
import { ProductGalleryMobileCarouselComponent } from './product-gallery/product-gallery-mobile-carousel.component';
import { ProductGalleryComponent } from './product-gallery/product-gallery.component';
import { ProductPriceComponent } from './product-price/product-price.component';
import { ProductRelatedComponent } from './product-related/product-related.component';
import { SeezFinanceCalculatorComponent } from './seez-finance-calculator/seez-finance-calculator.component';

@Component({
    selector: 'ncg-product-details',
    template: `
        <article class="product-details" *ngIf="product">
            <ngx-json-ld [json]="schema"></ngx-json-ld>
            <div class="product-details__go-back">
                <div class="product-details__container container">
                    <button type="button" class="button is-clean" (click)="onGoBack()">
                        <svg-icon-sprite
                            src="chevron-light-left"
                            [viewBox]="'0 0 30 30'"
                            [width]="'30px'"
                            [height]="'30px'"
                            aria-hidden="true"
                            class="mr-1"
                            classes=""
                        ></svg-icon-sprite>
                        <span>{{ 'product_details.back_to_overview_link' | translate }}</span>
                    </button>
                </div>
            </div>

            <div class="section pt-0 grid__row grid__theme">
                <div class="product-details__container container" aria-hidden="true">
                    <ncg-product-gallery [product]="product" [fallbackImage]="fallbackImage"></ncg-product-gallery>
                </div>

                <div class="container product-details__content">
                    <div class="columns product-details__columns">
                        <div class="column is-full-mobile is-8-desktop">
                            <div class="product-details__main">
                                <header class="product-details__header has-padding">
                                    <div class="product-details__title">
                                        <h1 class="product-details__title--main">{{ product.title }}</h1>
                                        <ncg-favorite-button
                                            class="product-details__favorite-button"
                                            [item]="product.properties"
                                        ></ncg-favorite-button>
                                        <ncg-product-item-tags
                                            class="product-details__tags"
                                            [item]="{ premium: product.properties.premium, isDemo: product.properties.isDemo }"
                                            [direction]="'column'"
                                        ></ncg-product-item-tags>
                                    </div>
                                    <h2 class="product-details__title--subtitle" *ngIf="product.variant">{{ product.variant }}</h2>
                                </header>

                                <div class="is-hidden-desktop">
                                    <ng-container
                                        *ngIf="
                                            (productPageService.productAdType$ | async) === 'leasing' &&
                                                findLeasingCalculatorData &&
                                                data.findLeasingPlacement === 'sidebar';
                                            else prices
                                        "
                                    >
                                        <ng-container *ngTemplateOutlet="leasing; context: { showButtons: true }"></ng-container>
                                    </ng-container>
                                </div>

                                <div class="product-details__description">
                                    <div class="product-details__comment block rte-content" *ngIf="product.properties.comment">
                                        <h4 class="block__header">{{ 'product_details.comment' | translate }}</h4>
                                        <p
                                            [@expandAnimation]="{
                                                value: expandedState,
                                                params: {
                                                    closedHeight: commentDescriptionHeight
                                                }
                                            }"
                                            class="product-details__comment-text is-preline"
                                            [class.is-collapsed]="!isDescriptionExpanded"
                                            [attr.data-collapsed]="!isDescriptionExpanded"
                                            [innerHTML]="product.properties.comment?.trim()"
                                        ></p>
                                        <button
                                            aria-label="toggles visibility for description text"
                                            *ngIf="isTextToggleVisible"
                                            type="button"
                                            class="button is-link"
                                            (click)="toggleText()"
                                        >
                                            <span *ngIf="!isDescriptionExpanded">{{ 'product_details.read_more_button' | translate }}</span>
                                            <span *ngIf="isDescriptionExpanded">{{ 'product_details.read_less_button' | translate }}</span>
                                            <div
                                                class="icon ml-1 chevron chevron--animate"
                                                aria-hidden="true"
                                                [ngClass]="{ 'chevron--up': isDescriptionExpanded }"
                                            >
                                                <svg-icon-sprite
                                                    src="chevron-light-down"
                                                    [viewBox]="'0 0 30 30'"
                                                    [width]="'30px'"
                                                    [height]="'30px'"
                                                    aria-hidden="true"
                                                ></svg-icon-sprite>
                                            </div>
                                        </button>
                                    </div>

                                    <div class="product-details__prominent-data block">
                                        <table class="data-table" aria-label="Prominent vehicle data">
                                            <tbody>
                                                <tr *ngIf="product.properties.year">
                                                    <th class="block__header">{{ 'product_details.year' | translate }}</th>
                                                    <td>{{ product.properties.year }}</td>
                                                </tr>
                                                <tr *ngIf="product.properties.registrationDate">
                                                    <th class="block__header">{{ 'product_details.registration_date' | translate }}</th>
                                                    <td>{{ product.properties.registrationDate | date: 'shortDate' : culture }}</td>
                                                </tr>
                                                <tr *ngIf="product.mileage">
                                                    <th class="block__header">{{ 'product_details.mileage' | translate }}</th>
                                                    <td>{{ product.mileage }}</td>
                                                </tr>
                                                <tr *ngIf="product.properties.gearType">
                                                    <th class="block__header">{{ 'product_details.gear_type' | translate }}</th>
                                                    <td>{{ product.properties.gearType }}</td>
                                                </tr>
                                                <tr *ngIf="product.properties.numberOfDoors">
                                                    <th class="block__header">{{ 'product_details.number_of_doors' | translate }}</th>
                                                    <td>{{ product.properties.numberOfDoors }}</td>
                                                </tr>
                                                <tr *ngIf="product.properties.numberOfPassengers">
                                                    <th class="block__header">{{ 'product_details.number_of_passengers' | translate }}</th>
                                                    <td>{{ product.properties.numberOfPassengers }}</td>
                                                </tr>
                                                <tr *ngIf="product.properties.propellant">
                                                    <th class="block__header">{{ 'product_details.propellant' | translate }}</th>
                                                    <td>{{ product.properties.propellant }}</td>
                                                </tr>
                                                <tr *ngIf="data.dealership">
                                                    <th class="block__header">{{ 'product_details.dealership' | translate }}</th>
                                                    <td>{{ data.dealership.location.name }}</td>
                                                </tr>
                                                <tr *ngIf="product.properties.color">
                                                    <th class="block__header">{{ 'product_details.color' | translate }}</th>
                                                    <td>{{ product.properties.color }}</td>
                                                </tr>
                                                <tr *ngIf="product.greenTax">
                                                    <th class="block__header">{{ 'product_details.cost_of_ownership' | translate }}</th>
                                                    <td>{{ product.greenTax }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <section
                                        class="is-hidden-tablet mb-5"
                                        *ngIf="
                                            (productPageService.productAdType$ | async) === 'cash' &&
                                            findLeasingCalculatorData &&
                                            data.findLeasingPlacement === 'sidebar'
                                        "
                                    >
                                        <ng-container *ngTemplateOutlet="leasing; context: { showButtons: false }"></ng-container>
                                    </section>

                                    <section
                                        class="product-details__video block is-hidden-tablet"
                                        *ngIf="product.properties.video"
                                        aria-hidden="true"
                                        #videoBlock
                                    >
                                        <h3 class="block__header">{{ 'product_details.video_mobile_header' | translate }}</h3>
                                        <div class="product-details__video-content" [class.is-blocked]="!isVideoAllowed">
                                            <iframe
                                                class="product-details__video-wrapper"
                                                [attr.src]="product.properties.video | safe: 'resourceUrl'"
                                                allowfullscreen
                                                allowtransparency
                                                mozallowfullscreen
                                                webkitallowfullscreen
                                                frameborder="0"
                                                title="Video player"
                                                *ngIf="isVideoAllowed"
                                            ></iframe>
                                            <div class="product-details__video-wrapper product-details__video-blocked" *ngIf="!isVideoAllowed">
                                                <p>{{ 'embed_spot.video_blocked_placeholder' | translate }}</p>
                                                <button class="button is-primary" (click)="onShowCookieBanner()" type="button">
                                                    {{ 'embed_spot.open_cookie' | translate }}
                                                </button>
                                            </div>
                                        </div>
                                    </section>

                                    <section class="product-details__premium" *ngIf="product.properties.premium && premiumContentSelection">
                                        <div class="columns is-multiline is-mobile columns is-variable is-0-mobile is-3-desktop">
                                            <div class="column is-8-desktop is-12-touch has-text-centered-touch">
                                                <header>
                                                    <h3 class="product-details__premium--header">
                                                        {{
                                                            'product_details.premium_certified_header'
                                                                | translate: { premiumText: product.properties.premium }
                                                        }}
                                                    </h3>
                                                </header>
                                                <div
                                                    class="product-details__premium--content rte-content"
                                                    *ngIf="premiumContentSelection?.teaserText"
                                                >
                                                    {{ premiumContentSelection?.teaserText }}
                                                </div>
                                                <button
                                                    type="button"
                                                    [attr.aria-label]="'Opens sidepanel with more details about ' + product.properties.premium!"
                                                    class="button is-link product-details__premium--link"
                                                    (click)="openSidepanel()"
                                                >
                                                    {{
                                                        'product_details.premium_certified_link_label'
                                                            | translate: { premiumText: product.properties.premium }
                                                    }}
                                                </button>
                                            </div>
                                            <div class="column is-4-desktop is-12-touch has-text-centered pb-0" aria-hidden="true">
                                                <img
                                                    class="product-details__premium--image"
                                                    alt="Quality logo"
                                                    src="assets/images/quality.svg"
                                                    width="60"
                                                    height="71"
                                                />
                                            </div>
                                        </div>
                                    </section>

                                    <div class="product-details__separator product-details__separator--long"></div>

                                    <div class="product-details__accordion">
                                        <ng-container *ngIf="data.usps?.length">
                                            <ncg-expansion-panel class="product-details__section">
                                                <h2 class="product-details__section--title" expansion-title>
                                                    {{ 'product_details.usp_header' | translate }}
                                                </h2>
                                                <img expansion-icon alt="icon" src="assets/images/down.svg" width="30" height="30" />
                                                <div class="product-details__section--content">
                                                    <div class="product-details__usps columns">
                                                        <div class="column is-4" *ngFor="let usp of data.usps; trackBy: trackByUsp">
                                                            <section class="product-details__usp block" *ngIf="usp">
                                                                <img
                                                                    aria-hidden="true"
                                                                    [attr.alt]="usp.icon.altText || usp.title"
                                                                    [attr.src]="imageUrl(usp.icon, { width: 104, height: 104, mode: 'crop' })"
                                                                    [attr.srcset]="
                                                                        imageUrl(usp.icon, { width: 104, height: 104, mode: 'crop' }) +
                                                                        ' 1x,' +
                                                                        imageUrl(usp.icon, { width: 208, height: 208, mode: 'crop' }) +
                                                                        ' 2x'
                                                                    "
                                                                    sizes="104px"
                                                                    height="104"
                                                                    width="104"
                                                                    loading="lazy"
                                                                    *ngIf="usp.icon"
                                                                />
                                                                <header class="block__header" *ngIf="usp.title">{{ usp.title }}</header>
                                                                <p class="rte-content" *ngIf="usp.teaser">
                                                                    {{ usp.teaser }}
                                                                </p>
                                                                <p class="rte-content">
                                                                    <a
                                                                        [routerLink]="usp.link.url"
                                                                        [title]="usp.linkHoverTitle"
                                                                        [attr.aria-label]="usp.linkHoverTitle"
                                                                        *ngIf="usp.link"
                                                                        >{{ usp.link.name }}</a
                                                                    >
                                                                </p>
                                                            </section>
                                                        </div>
                                                    </div>
                                                </div>
                                            </ncg-expansion-panel>

                                            <div class="product-details__separator"></div>
                                        </ng-container>

                                        <ncg-seez-finance-calculator
                                            *ngIf="
                                                settings?.seezSetup?.enabled &&
                                                data?.enableFinanceCalculator &&
                                                (productPageService.productAdType$ | async) === 'cash' &&
                                                financeCalculatorData?.listingId
                                            "
                                            [data]="financeCalculatorData"
                                        ></ncg-seez-finance-calculator>

                                        <ng-container *ngIf="equipmentList?.length">
                                            <ncg-expansion-panel class="product-details__section">
                                                <h2 class="product-details__section--title" expansion-title>
                                                    {{ 'product_details.equipment_header' | translate }}
                                                </h2>
                                                <img expansion-icon alt="icon" src="assets/images/down.svg" width="30" height="30" />
                                                <div class="product-details__section--content">
                                                    <ul class="product-details__equipment-list">
                                                        <li class="product-details__equipment-value" *ngFor="let equipment of equipmentList">
                                                            {{ equipment }}
                                                        </li>
                                                    </ul>
                                                </div>
                                            </ncg-expansion-panel>

                                            <div class="product-details__separator"></div>
                                        </ng-container>

                                        <ng-container *ngIf="product.properties">
                                            <ncg-expansion-panel class="product-details__section">
                                                <h2 class="product-details__section--title" expansion-title [id]="'table-' + product.properties.id">
                                                    {{ 'product_details.specifications_header' | translate }}
                                                </h2>
                                                <img expansion-icon alt="icon" src="assets/images/down.svg" width="30" height="30" />
                                                <div class="product-details__section--content">
                                                    <table
                                                        class="product-details__specs-table"
                                                        [attr.aria-describedby]="'table-' + product.properties.id"
                                                    >
                                                        <tbody>
                                                            <tr *ngIf="product.properties.year">
                                                                <th class="block__header">{{ 'product_details.year' | translate }}</th>
                                                                <td>{{ product.properties.year }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.color">
                                                                <th class="block__header">{{ 'product_details.color' | translate }}</th>
                                                                <td>{{ product.properties.color }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.registrationDate">
                                                                <th class="block__header">{{ 'product_details.registration_date' | translate }}</th>
                                                                <td>{{ product.properties.registrationDate | date: 'shortDate' : culture }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.mileage">
                                                                <th class="block__header">{{ 'product_details.mileage' | translate }}</th>
                                                                <td>{{ product.mileage }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.acceleration0To100">
                                                                <th class="block__header">{{ 'product_details.0_to_100' | translate }}</th>
                                                                <td>
                                                                    {{ product.properties.acceleration0To100 | number: '1.0-1' : culture }}
                                                                    s
                                                                </td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.topSpeed">
                                                                <th class="block__header">{{ 'product_details.topspeed' | translate }}</th>
                                                                <td>{{ product.properties.topSpeed | number: '1.0-0' : culture }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.numberOfDoors">
                                                                <th class="block__header">{{ 'product_details.number_of_doors' | translate }}</th>
                                                                <td>{{ product.properties.numberOfDoors }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.numberOfPassengers">
                                                                <th class="block__header">
                                                                    {{ 'product_details.number_of_passengers' | translate }}
                                                                </th>
                                                                <td>{{ product.properties.numberOfPassengers }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.numberOfGears">
                                                                <th class="block__header">{{ 'product_details.number_of_gears' | translate }}</th>
                                                                <td>{{ product.properties.numberOfGears }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.gearType">
                                                                <th class="block__header">{{ 'product_details.gear_type' | translate }}</th>
                                                                <td>{{ product.properties.gearType }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.propellant">
                                                                <th class="block__header">{{ 'product_details.propellant' | translate }}</th>
                                                                <td>{{ product.properties.propellant }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.motor">
                                                                <th class="block__header">{{ 'product_details.motor' | translate }}</th>
                                                                <td>{{ product.motor }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.cylinders">
                                                                <th class="block__header">{{ 'product_details.cylinders' | translate }}</th>
                                                                <td>{{ product.properties.cylinders }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.cylinderVolumeCC">
                                                                <th class="block__header">{{ 'product_details.cylindervolumecc' | translate }}</th>
                                                                <td>
                                                                    {{ product.properties.cylinderVolumeCC | number: '1.0-0' : culture }}
                                                                </td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.effect">
                                                                <th class="block__header">{{ 'product_details.effect' | translate }}</th>
                                                                <td>{{ product.properties.effect | number: '1.0-0' : culture }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.effectInNm">
                                                                <th class="block__header">{{ 'product_details.effectinnm' | translate }}</th>
                                                                <td>{{ product.properties.effectInNm | number: '1.0-0' : culture }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.effectInKw">
                                                                <th class="block__header">{{ 'product_details.effectinkw' | translate }}</th>
                                                                <td>{{ product.properties.effectInKw | number: '1.0-0' : culture }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.co2Emissions">
                                                                <th class="block__header">{{ 'product_details.co2_emissions' | translate }}</th>
                                                                <td>{{ product.properties.co2Emissions }} g CO2/km</td>
                                                            </tr>
                                                            <ng-container *ngIf="product.properties.propellant === 'El'; else defaultRange">
                                                                <tr *ngIf="product.properties.electricConsumption as electricConsumption">
                                                                    <th class="block__header">
                                                                        {{ 'product_details.watt_hours_per_km' | translate }}
                                                                    </th>
                                                                    <td>{{ electricConsumption | number: '1.0-1' : culture }}</td>
                                                                </tr>
                                                            </ng-container>
                                                            <ng-template #defaultRange>
                                                                <tr *ngIf="product.properties.kmPerLiter as kmPerLiter">
                                                                    <th class="block__header">{{ 'product_details.km_per_liter' | translate }}</th>
                                                                    <td>{{ kmPerLiter | number: '1.0-1' : culture }}</td>
                                                                </tr>
                                                            </ng-template>
                                                            <tr *ngIf="product.properties.driveWheels">
                                                                <th class="block__header">{{ 'product_details.drivewheels' | translate }}</th>
                                                                <td>{{ product.properties.driveWheels }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.length">
                                                                <th class="block__header">{{ 'product_details.length' | translate }}</th>
                                                                <td>{{ product.properties.length | number: '1.0-0' : culture }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.width">
                                                                <th class="block__header">{{ 'product_details.width' | translate }}</th>
                                                                <td>{{ product.properties.width | number: '1.0-0' : culture }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.height">
                                                                <th class="block__header">{{ 'product_details.height' | translate }}</th>
                                                                <td>{{ product.properties.height | number: '1.0-0' : culture }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.trunkSize">
                                                                <th class="block__header">{{ 'product_details.trunk_size' | translate }}</th>
                                                                <td>
                                                                    {{ product.properties.trunkSize | number: '1.0-0' : culture }}
                                                                    l
                                                                </td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.weight">
                                                                <th class="block__header">{{ 'product_details.weight' | translate }}</th>
                                                                <td>{{ product.properties.weight | number: '1.0-0' : culture }}</td>
                                                            </tr>
                                                            <tr *ngIf="product.properties.trailerWeight">
                                                                <th class="block__header">{{ 'product_details.trailer_weight' | translate }}</th>
                                                                <td>{{ product.properties.trailerWeight | number: '1.0-0' : culture }}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                    <p></p>
                                                    <p>
                                                        <small>{{ 'product_details.input_accuracy_notice' | translate }}</small>
                                                    </p>
                                                </div>
                                            </ncg-expansion-panel>

                                            <div class="product-details__separator"></div>
                                        </ng-container>

                                        <ng-container *ngIf="data.testDrive && isTestDriveEnabled">
                                            <ncg-expansion-panel class="product-details__section">
                                                <h2 class="product-details__section--title" expansion-title>
                                                    {{ 'product_details.testdrive_header' | translate }}
                                                </h2>
                                                <img expansion-icon alt="icon" src="assets/images/down.svg" width="30" height="30" />
                                                <div class="product-details__section--content">
                                                    <ncg-rich-text [html]="data.testDrive"></ncg-rich-text>
                                                    <ncg-test-drive-button [buttonClass]="'button is-primary'"></ncg-test-drive-button>
                                                </div>
                                            </ncg-expansion-panel>

                                            <div class="product-details__separator"></div>
                                        </ng-container>

                                        <ng-container *ngIf="data.dealership">
                                            <ncg-expansion-panel class="product-details__section">
                                                <h2 class="product-details__section--title" expansion-title>
                                                    {{ data.dealership.location.name }}
                                                </h2>
                                                <img expansion-icon alt="icon" src="assets/images/down.svg" width="30" height="30" />
                                                <div class="product-details__section--content">
                                                    <p class="is-preline" *ngIf="data.dealership.dealerPage?.teaser">
                                                        {{ data.dealership.dealerPage?.teaser }}
                                                    </p>
                                                    <ncg-dealer-info [location]="data.dealership.location"></ncg-dealer-info>
                                                </div>
                                            </ncg-expansion-panel>

                                            <div class="product-details__separator"></div>
                                        </ng-container>

                                        <ng-container *ngIf="data.snippet && data.snippetTitle">
                                            <ncg-expansion-panel class="product-details__section">
                                                <h2 class="product-details__section--title" expansion-title>
                                                    {{ data.snippetTitle }}
                                                </h2>
                                                <img expansion-icon alt="icon" src="assets/images/down.svg" width="30" height="30" />
                                                <div class="product-details__section--content">
                                                    <ng-container *ngIf="showSnippetWarning">
                                                        <h4 class="pb-4" *ngIf="data.snippetWarningText">{{ data.snippetWarningText }}</h4>
                                                        <button type="button" class="button is-primary" (click)="onShowCookieBanner()">
                                                            {{ data.snippetWarningButton }}
                                                        </button>
                                                    </ng-container>
                                                    <ng-container *ngIf="!showSnippetWarning">
                                                        <div class="snippet" [innerHTML]="data.snippet | safe: 'html'"></div>
                                                        <ng-template
                                                            *ngIf="data.snippet.includes('ap-embed')"
                                                            ncgLazyComponentLoader
                                                            [alias]="lazyContentAliases.AutoProff"
                                                        ></ng-template>
                                                    </ng-container>
                                                </div>
                                            </ncg-expansion-panel>
                                            <div class="product-details__separator"></div>
                                        </ng-container>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="column is-4 is-hidden-mobile is-hidden-tablet-only product-details__sidebar">
                            <ng-container *ngIf="data.findLeasingPlacement === 'main'">
                                <ng-container *ngTemplateOutlet="prices"></ng-container>
                            </ng-container>
                            <ng-container *ngIf="data.findLeasingPlacement === 'sidebar'">
                                <ng-container *ngIf="(productPageService.productAdType$ | async) === 'cash'">
                                    <ng-container *ngTemplateOutlet="prices"></ng-container>
                                </ng-container>
                                <ng-container *ngIf="findLeasingCalculatorData">
                                    <ng-container
                                        *ngTemplateOutlet="
                                            leasing;
                                            context: { showButtons: (productPageService.productAdType$ | async) === 'leasing' }
                                        "
                                    ></ng-container>
                                </ng-container>
                            </ng-container>
                        </div>
                    </div>
                    <div class="product-details__calculator" *ngIf="findLeasingCalculatorData && data.findLeasingPlacement === 'main'">
                        <ng-template
                            ncgLazyComponentLoader
                            [data]="findLeasingCalculatorData"
                            [alias]="lazyContentAliases.FindLeasingCalculator"
                        ></ng-template>
                    </div>
                </div>
            </div>

            <ncg-recently-viewed-cars-spot [productId]="productId" [fallbackImage]="fallbackImage"></ncg-recently-viewed-cars-spot>

            <ncg-product-related [productId]="productId"></ncg-product-related>

            <ng-template #leasing let-showButtons="showButtons">
                <div class="product-details__leasing-wrapper">
                    <ng-template
                        ncgLazyComponentLoader
                        [data]="findLeasingCalculatorData"
                        [alias]="lazyContentAliases.FindLeasingCalculator"
                    ></ng-template>
                </div>
                <div class="product-details__prices-buttons" *ngIf="showButtons">
                    <ncg-send-offer-button [buttonClass]="'button is-primary is-fullwidth'"></ncg-send-offer-button>
                    <ncg-seez-finance-calculator-button
                        *ngIf="settings?.seezSetup?.enabled && data?.enableFinanceCalculator && financeCalculatorData?.listingId"
                        [buttonClass]="'button is-primary is-fullwidth'"
                        [buttonText]="data.financeCalculatorButtonText"
                        [data]="financeCalculatorData"
                    ></ncg-seez-finance-calculator-button>
                    <ncg-test-drive-button [buttonClass]="'button is-primary is-fullwidth'"></ncg-test-drive-button>
                    <ncg-favorites-page-link [iconClass]="'is-hidden'" [linkClass]="'is-fullwidth'"></ncg-favorites-page-link>
                </div>
            </ng-template>

            <ng-template #prices>
                <div class="product-details__prices-wrapper">
                    <div class="product-details__prices">
                        <ncg-product-price
                            [product]="product"
                            [cashPricePsa]="data.cashPricePsa"
                            [cashPriceNoVatPSA]="data.cashPriceNoVatPsa"
                            [leasingPricePsa]="data.leasingPricePsa"
                            [leasingPriceNoVatPsa]="data.leasingPriceNoVatPsa"
                            [callForPricePsa]="data.callForPricePsa"
                            [callForLeasingPsa]="data.callForLeasingPsa"
                            [hideLeasingTexts]="data.hideLeasingTexts"
                        ></ncg-product-price>
                    </div>
                </div>
            </ng-template>
        </article>
    `,
    animations: [expansionAnimation],
})
export class ProductDetailsComponent implements OnInit, OnDestroy {
    private readonly unsubscribe = new Subject<void>();

    @Input() data: IProductPageResponse;

    lazyContentAliases = LazyContentAliases;
    financeCalculatorData?: ISeezFinanceCalculator;
    findLeasingCalculatorData?: IFindLeasingCalculator;
    showSnippetWarning: boolean = false;
    product: IProductViewModel;
    productId: string;
    equipmentList: string[];
    settings: ISettingsResponse;
    premiumContentSelection?: IPremiumContentSelection;
    productLeasingType = ProductLeasingType;
    schema: any = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        'makesOffer': {
            '@type': 'Offer',
        },
    };
    fallbackImage?: IImage;
    isVideoAllowed = false;
    imageUrl = ImageUrl;
    isDescriptionExpanded = false;
    isTextToggleVisible = false;
    expandedState?: 'expanded' | 'closed';
    commentDescriptionHeight: string;
    isTestDriveEnabled = false;
    culture: string;

    constructor(
        private readonly translateService: TranslateService,
        private readonly metaService: MetaService,
        private readonly settingsService: SettingsService,
        private readonly sidePanelService: SidePanelService,
        private readonly trackingService: TrackingService,
        private readonly router: Router,
        private readonly cd: ChangeDetectorRef,
        private readonly featureDetectionService: FeatureDetectionService,
        private readonly location: Location,
        private readonly seezService: SeezService,
        public readonly productPageService: ProductPageService
    ) {}

    public ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public async ngOnInit() {
        this.settingsService
            .get()
            .pipe(take(1), takeUntil(this.unsubscribe))
            .subscribe((settings) => {
                this.settings = settings;
                this.fallbackImage = settings.productFallbackImage;

                if (this.data.product && this.data.product.properties.id) {
                    this.product = this.data.product;
                    this.productId = this.data.product.properties.id;
                    this.culture = `${settings.seoLanguage}-${settings.seoCountry}`;
                    this.setEquipmentList();
                    this.toggleTestDrive();
                    this.setPremiumUrl();
                    this._setJsonLd();
                    this.setDescriptionExpansion(this.product.properties.comment?.trim() ?? '');
                }

                this.cd.markForCheck();
            });

        this.trackingService
            .marketingConsentGiven()
            .pipe(
                distinctUntilChanged((v) => v !== this.isVideoAllowed),
                takeUntil(this.unsubscribe)
            )
            .subscribe((consent) => {
                this.isVideoAllowed = consent;
                this.cd.markForCheck();
            });

        // Cookie consent handling for "snippet".
        if (this.data && this.data.snippetWarningEnabled && this.data.snippetBlockers?.length) {
            const requiredConsents: Observable<boolean>[] = [];
            if (this.data.snippetBlockers.includes('Functional')) {
                requiredConsents.push(this.trackingService.functionalConsentGiven());
            }
            if (this.data.snippetBlockers.includes('Marketing')) {
                requiredConsents.push(this.trackingService.marketingConsentGiven());
            }
            if (this.data.snippetBlockers.includes('Statistic')) {
                requiredConsents.push(this.trackingService.statisticConsentGiven());
            }
            combineLatest(requiredConsents)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe((data) => {
                    this.showSnippetWarning = data.some((b) => b === false);
                    this.cd.markForCheck();
                });
        }

        // Seez load & set data for components.
        if (this.featureDetectionService.isBrowser() && this.data) {
            const { product, dealership } = await firstValueFrom(this.productPageService.productPageData$);
            const carBusinessId = dealership?.location.business?.identifier;
            const productId = product?.properties?.id;

            if (productId && carBusinessId) {
                this.seezService.initSeezSdk(carBusinessId).then(async (isLoaded) => {
                    if (isLoaded) {
                        const seezListingId = await this.seezService.getSeezListingId(productId);
                        if (seezListingId) {
                            this.financeCalculatorData = {
                                infoText: this.data.financeCalculatorText,
                                applyButtonText: this.data.financeCalculatorApplyNowButton,
                                productId,
                                listingId: seezListingId,
                            };
                        }
                    }
                    this.cd.markForCheck();
                });
            }
        }

        // Findleasing.dk
        // Even though leasingType can be noLeasing, the calculator should still be shown, because the same
        // vehicle could have a corresponding leasing ad. The calculator does the lookup and decides whether to render or not.
        if (this.data && this.data.findLeasingId && this.data.product && this.data.product.properties.id) {
            const isOperational = [ProductLeasingType.businessOperational, ProductLeasingType.privateOperational];
            const isBusiness = [ProductLeasingType.businessFinancial, ProductLeasingType.businessOperational];
            this.findLeasingCalculatorData = {
                sellerId: this.data.findLeasingId,
                carId: this.data.product.properties.id,
                funding: isOperational.includes(this.data.product.leasingType) ? 'operational' : 'financial',
                tax: isBusiness.includes(this.data.product.leasingType) ? '0' : '1',
                disclaimer: this.data.findLeasingDisclaimer,
                layout: this.data.findLeasingPlacement === 'sidebar' ? 'boxed' : 'plain',
            };
        }

        this.cd.markForCheck();
    }

    public toggleText() {
        this.isDescriptionExpanded = !this.isDescriptionExpanded;
        this.expandedState = this.isDescriptionExpanded ? 'expanded' : 'closed';
    }

    public openSidepanel() {
        if (this.premiumContentSelection?.contentLink?.url) {
            this.sidePanelService.openContent(this.premiumContentSelection?.contentLink?.url);
        }
    }

    public onGoBack() {
        if (this.metaService.previousPageRef === 'filterPage') {
            this.location.back();
        } else {
            this.router.navigateByUrl(this.settings.usedCarsLink.url);
        }
    }

    public onShowCookieBanner() {
        this.trackingService.showCookieBanner();
    }

    public trackByUsp(index: number, item: IProductUspViewModel) {
        return item.title || index;
    }

    private setDescriptionExpansion(description: string) {
        if (this.featureDetectionService.isBrowser()) {
            this.commentDescriptionHeight = getComputedStyle(document.body).getPropertyValue('--product-details-description-height') || '10rem';
            this.isTextToggleVisible = description.length > 140 && description.length - 140 > 80;
            if (!this.isTextToggleVisible) {
                this.isDescriptionExpanded = true;
            }

            this.expandedState = this.isDescriptionExpanded ? 'expanded' : 'closed';
        }
    }

    private toggleTestDrive() {
        const categoryCode = this.data.dealership?.location?.category?.code;
        this.isTestDriveEnabled = categoryCode === 'retail';
    }

    private setPremiumUrl() {
        if (this.product.properties.premium) {
            this.premiumContentSelection = this.settings.premiumContentSelection.find(
                (item) => item.make?.toLowerCase().trim() === this.product.properties.make?.toLowerCase().trim()
            );
        }
    }

    private async setEquipmentList(): Promise<void> {
        const equipmentList = this.product.properties.equipmentList;
        if (!equipmentList || !Array.isArray(equipmentList)) {
            return;
        }

        const translations = await firstValueFrom(this.translateService.get('product_equipment'))
            .then((x) => (typeof x === 'object' ? x : {}))
            .catch(() => {});

        this.equipmentList = equipmentList.map((x) =>
            (translations as Record<string, any>).hasOwnProperty(x.toLowerCase()) ? translations[x.toLowerCase()] : x
        );

        this.cd.markForCheck();
    }

    private _setJsonLd(): void {
        if (!this.data || !this.data.product) {
            return;
        }

        const { product, dealership } = this.data;
        const { properties } = product;

        this.schema.name = dealership?.location.name ?? this.settings.companyName;
        const car: any = {
            '@type': 'Car',
        };

        car.sku = this.productId;
        car.name = `${properties.year} ${product.properties.displayName}`.trim();
        car.brand = properties.make || 'Unknown';
        car.vehicleTransmission = 'gearbox';

        if (properties.variant) {
            car.vehicleConfiguration = properties.variant;
        }

        if (properties.registrationDate) {
            car.dateVehicleFirstRegistered = properties.registrationDate;
        }

        if (properties.color) {
            car.color = properties.color;
        }

        if (properties.numberOfGears) {
            car.numberOfForwardGears = properties.numberOfGears;
        }

        if (properties.numberOfAirbags) {
            car.numberOfAirbags = properties.numberOfAirbags;
        }

        if (properties.numberOfDoors) {
            car.numberOfDoors = properties.numberOfDoors;
        }

        if (properties.productionYear) {
            car.productionDate = properties.productionYear.toString();
        }

        if (properties.driveWheels) {
            let driveWheelConfiguration = '';

            if (properties.driveWheels === '4') {
                driveWheelConfiguration = 'AllWheelDriveConfiguration';
            } else if (properties.driveWheels === 'B') {
                driveWheelConfiguration = 'RearWheelDriveConfiguration';
            } else if (properties.driveWheels === 'F') {
                driveWheelConfiguration = 'FrontWheelDriveConfiguration';
            }

            if (driveWheelConfiguration) {
                car.driveWheelConfiguration = {
                    '@type': 'QualitativeValue',
                    'valueReference': driveWheelConfiguration,
                };
            }
        }

        if (properties.bodyTypes?.length) {
            car.bodyType = properties.bodyTypes[0];
        }

        const engineSpecification: any = {
            '@type': 'EngineSpecification',
        };

        if (properties.motor) {
            engineSpecification.engineDisplacement = {
                '@type': 'QuantitativeValue',
                'unitCode': 'LTR',
                'value': properties.motor,
            };
        }

        if (properties.effect) {
            engineSpecification.enginePower = {
                '@type': 'QuantitativeValue',
                'unitCode': 'BHP',
                'value': properties.effect,
            };
        }

        if (properties.propellant === 'El') {
            engineSpecification.engineType = 'Electric';
        }

        if (properties.effectInNm) {
            engineSpecification.torque = {
                '@type': 'QuantitativeValue',
                'unitCode': 'NU',
                'value': properties.effectInNm,
            };
        }

        car.vehicleEngine = engineSpecification;

        if (properties.acceleration0To100) {
            car.accelerationTime = {
                '@type': 'QuantitativeValue',
                'name': 'KMH',
                'unitCode': 'SEC',
                'value': properties.acceleration0To100,
            };
        }

        if (properties.mileage) {
            car.mileageFromOdometer = {
                '@type': 'QuantitativeValue',
                'unitCode': properties.culture?.toLowerCase() === 'sv-se' ? 'SMI' : 'KMT',
                'value': properties.mileage,
            };
        }

        if (properties.totalWeight && properties.weight) {
            car.payload = {
                '@type': 'QuantitativeValue',
                'unitCode': 'KG',
                'value': Number(properties.totalWeight) - Number(properties.weight),
            };
            car.weight = {
                '@type': 'QuantitativeValue',
                'unitCode': 'KG',
                'value': properties.weight,
            };
            car.weightTotal = {
                '@type': 'QuantitativeValue',
                'unitCode': 'KG',
                'value': properties.totalWeight,
            };
        }

        if (properties.width) {
            car.width = {
                '@type': 'QuantitativeValue',
                'unitCode': 'CM',
                'value': properties.width,
            };
        }

        if (properties.height) {
            car.height = {
                '@type': 'QuantitativeValue',
                'unitCode': 'CM',
                'value': properties.height,
            };
        }

        if (properties.length) {
            car.depth = {
                '@type': 'QuantitativeValue',
                'unitCode': 'CM',
                'value': properties.length,
            };
        }

        if (properties.topSpeed) {
            car.speed = {
                '@type': 'QuantitativeValue',
                'unitCode': 'KMH',
                'minValue': 0,
                'maxValue': properties.topSpeed,
            };
        }

        if (properties.trailerWeight) {
            car.trailerWeight = {
                '@type': 'QuantitativeValue',
                'unitCode': 'KG',
                'value': properties.trailerWeight,
            };
        }

        if (properties.cashPrice && product.priceType !== 'CallForPrice') {
            car.offers = {
                '@type': 'Offer',
                'price': properties.cashPrice,
                'priceCurrency': 'DKK',
                'availability': 'InStock',
            };
        }

        if (properties.comment) {
            car.description = properties.comment;
        }

        if (properties.pictures?.length) {
            car.image = properties.pictures[0];
        }

        this.schema.makesOffer.priceSpecification = {
            '@type': 'UnitPriceSpecification',
            'priceCurrency': 'DKK',
            'price': properties.cashPrice,
        };

        this.schema.makesOffer.itemOffered = car;
    }
}

@NgModule({
    declarations: [
        ProductDetailsComponent,
        ProductGalleryComponent,
        ProductPriceComponent,
        ProductGalleryCarouselComponent,
        ProductGalleryMobileCarouselComponent,
        ProductRelatedComponent,
    ],
    imports: [
        CommonModule,
        TranslateModule.forRoot(),
        RouterModule,
        UtilsModule,
        RichTextModule,
        ExpansionPanelModule,
        DealerModule,
        NgxJsonLdModule,
        BrowserAnimationsModule,
        SwiperModule,
        IconSpriteModule,
        SpotsUiModule,
        FavoritesServiceModule,
        FavoritesPageLinkComponent,
        ProductItemTagsComponent,
        LazyContentModule,
        SendOfferButtonComponent,
        TestDriveButtonComponent,
        GetPriceButtonComponent,
        SeezBuyButtonComponent,
        SeezFinanceCalculatorButtonComponent,
        SeezFinanceCalculatorComponent,
    ],
})
export class ProductDetailsModule {}
